<template>
    <v-app class="app">
        <div class="nav-icon-collapsed">
            <!-- On a phone the menu is closed most of the time, so its toggle
                 carries the dot when work is waiting on Files or Production
                 counts (docs/update-designs-proposal.md §5.7), or when a
                 notification is unread. -->
            <v-badge v-if="show_drawer" :model-value="menu_dot" dot color="accent" offset-x="8" offset-y="8">
                <v-btn variant="text" :icon="drawer ? 'mdi-chevron-left' : 'mdi-chevron-right'"
                    @click.stop="switch_drawer">
                </v-btn>
            </v-badge>
        </div>
        <v-navigation-drawer v-if="show_drawer" :permanent="!isPhone" :temporary="isPhone"
            :rail="!isPhone && !drawer" :touchless="!isPhone" :model-value="!isPhone || drawer"
            @update:model-value="onDrawerModel" :width="drawer_width" app>
            <LeftMenu :rail="!isPhone && !drawer" />
        </v-navigation-drawer>
        <v-main>
            <router-view></router-view>
            <Notifications />
            <DialogQrReader />
        </v-main>
        <!-- App-wide activity indicator. Any store loader lights it up, which
             is what makes a project load - seven paginated calls, several
             seconds on a big one - visible from the moment it starts. It never
             covers the screen: on a shop-floor tablet a blocking overlay eats
             the operator's taps and swallows the Android back button. -->
        <v-progress-linear :active="loading" indeterminate color="accent" height="4" class="app-loading-bar" />

    </v-app>
</template>

<style lang="scss">
// set left padding for <ul> and <li> elements
// in all texts in the app
ul {
    padding-left: 20px;
}


.app {
    height: 100vh;
}

.nav-icon-collapsed {
    position: fixed;
    top: 12px;
    left: 5px;
    z-index: 2000;
}

// Spans the whole window, drawer included, so it reads the same wherever the
// user is looking. Below dialogs and menus (2000+), above the drawer (~1005).
.app-loading-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1010;
}

// Neutral thumb taken from the theme's text colour, so it is dark grey on light
// surfaces and light grey on dark ones, and never the accent: the accent is for
// actions (docs/scrollbar-color-proposal.md). Declared on the theme classes too,
// so a v-theme-provider (the login page) gets its own colour and not the root's.
:root,
.v-theme--light,
.v-theme--dark {
    scrollbar-color: rgba(var(--v-theme-on-surface), 0.3) transparent;
}

// scrollbar-width is not inherited (scrollbar-color is), so on :root it only
// reached the window: the drawer, tables and dialogs kept the 15 px bar.
* {
    scrollbar-width: thin;
}

// Chrome ignores ::-webkit-scrollbar wherever scrollbar-color is set, so these
// only serve browsers without scrollbar-color (older Safari).
@supports not (scrollbar-color: auto) {
    ::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    ::-webkit-scrollbar-thumb {
        background: rgba(var(--v-theme-on-surface), 0.3);
        border-radius: 4px;
    }
}
</style>

<script>
import LeftMenu from "./components/left_menu/LeftMenu.vue";
import DialogQrReader from "./components/left_menu/DialogQrReader.vue";
import { useAppStore } from "./stores/app";
import { useSessionStore } from "./stores/session";
import { useNotificationsStore } from "./stores/notifications";
import Notifications from "/src/components/Notifications";
import { useTheme } from 'vuetify'
import { usePitStoreBasics } from "@/stores/app_common";
import bus from '/src/utils/event_bus';
import { takeMicrosoftResult } from '/src/utils/oauth';
import { useIsPhone } from '@/composables/display';
import { useSelectedStore } from './stores/selected';
import { anyIndicator } from '/src/utils/design_update.js';

// produuz.it orange, used when the company has no corporate color.
const DEFAULT_ACCENT = "#ea590c";


export default {
    setup() {
        const theme = useTheme()
        return { ...usePitStoreBasics(), theme, ...useIsPhone() }
    },
    components: {
        LeftMenu,
        DialogQrReader,
        Notifications,
    },
    data: () => ({
        drawer: true,
    }),
    computed: {
        loading() {
            return this.app_store.loading;
        },
        show_drawer() {
            // The factory screen is for a TV: nothing but the figures.
            return !["login", "dashboard_screen"].includes(this.$route.name);
        },
        company_color() {
            return useSessionStore().user_object?.auth?.company
                ?.meta?.company_color;
        },
        use_corporate_color() {
            const v = useSessionStore().user_object?.auth?.company
                ?.meta?.use_corporate_color;
            // Unset means enabled: a company that registered a corporate color
            // expects it to be used without ticking a second checkbox.
            return v === undefined || v === null ? true : !!v;
        },
        // The accent actually in use. Falls back to the produuz.it orange when
        // the company has no color registered, so we never push an empty value
        // into the theme (Vuetify would fail to parse it and break every color).
        accent_color() {
            const c = this.company_color;
            if (this.use_corporate_color && typeof c === "string" && c.trim()) {
                return c.trim();
            }
            return DEFAULT_ACCENT;
        },
        iam_prod_worker() {
            return useSessionStore().iam_prod_worker;
        },
        menu_dot() {
            if (!this.isPhone || this.drawer) return false;
            // The bell is in the menu too: an unread notification is work
            // waiting in every mode (docs/notification-bell-proposal.md §4.4).
            if (useNotificationsStore().unread_count > 0) return true;
            if (this.app_store.app_mode != "manage") return false;
            const selected_store = useSelectedStore();
            return anyIndicator(selected_store.project_attention, selected_store.attentionQuantities);
        },
        drawer_width() {
            // On phones the overlay drawer leaves room for the scrim tap;
            // on >=600px this evaluates to the historical 400px.
            return this.isPhone ? Math.min(400, this.displayWidth - 48) : 400;
        },
    },
    watch: {
        accent_color: {
            handler: function (color) {
                this.set_accent_color(color);
            },
            immediate: true,
        },
        iam_prod_worker: {
            handler(iam) {
                this.drawer = !iam && !this.isPhone
            },
            immediate: true
        },
    },
    methods: {
        switch_drawer() {
            this.drawer = !this.drawer;
        },
        onDrawerModel(open) {
            // Only the phone overlay drives the model (e.g. scrim tap to close);
            // on desktop/tablet the drawer is permanent and stays mounted.
            if (this.isPhone) this.drawer = open;
        },
        set_accent_color(color) {
            this.theme.themes.value.dark.colors.accent = color
            this.theme.themes.value.light.colors.accent = color
        },
        // Notifies the outcome of a Microsoft redirect (login/link) that was
        // already completed in main.js by consumeMicrosoftRedirect (tokens are
        // set / identity linked before the app mounts).
        handleMicrosoftRedirect() {
            const r = takeMicrosoftResult();
            if (!r) return;
            if (r.ok) {
                bus.emit("notification", { timeout: 2000, text: r.mode === "link" ? "Microsoft account linked." : "Logged in.", color: "success" });
                if (r.mode === "login") this.$router.push({ name: "viewer" }).catch(() => { });
                return;
            }
            let text = r.mode === "link" ? "Can't link this account." : "Can't sign in with this account.";
            if (r.error === "no_account") text = "No account found for this email. Ask your administrator to create one.";
            else if (r.error === "ambiguous_account") text = "This email matches several accounts. Please contact support.";
            else if (r.error === "already_linked_elsewhere") text = "This account is already linked to another user.";
            else text += ` (${r.error})`;
            bus.emit("notification", { timeout: 5000, text, color: "error" });
        }
    },
    mounted() {
        this.handleMicrosoftRedirect()
        useSessionStore().refresh()
        const app_store = useAppStore()

        // First load
        app_store.initialLoad() // only initial
        app_store.refreshBaseUserData(false)
        // The bell's unread count: one timer for the whole app.
        useNotificationsStore().startPolling()

        // 2 minutes loads
        this._refreshInterval = setInterval(
            () => app_store.refreshBaseUserData(false),
            120000
        );

        // Stop polling on logout
        this._onLogout = () => {
            if (this._refreshInterval) {
                clearInterval(this._refreshInterval)
                this._refreshInterval = null
            }
        }
        bus.on("events/session/logout", this._onLogout)
    },
    beforeUnmount() {
        if (this._refreshInterval) {
            clearInterval(this._refreshInterval)
        }
        bus.off("events/session/logout", this._onLogout)
    }
};
</script>
